if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      connectedCallback() {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;

          console.log('Variant change detected in product form, updating button price', event.data.variant);
          this.updateButtonPrice(event.data.variant);
        });
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      updateButtonPrice(variant) {
        if (!variant) return;

        // Try multiple selectors to find the price button element
        let buttonPriceElement = this.querySelector(`#price-button-${this.sectionId}`);
        
        // Fallback: try without "button" in ID (for legacy templates)
        if (!buttonPriceElement) {
          buttonPriceElement = this.querySelector(`#price-${this.sectionId}`);
        }
        
        // Fallback: try finding by ID prefix
        if (!buttonPriceElement) {
          buttonPriceElement = this.querySelector('[id^="price-button-"]') || this.querySelector('[id^="price-"]');
        }

        // If still not found, create the price element
        if (!buttonPriceElement) {
          const submitButton = this.querySelector('button[type="submit"]');
          const buyBtnSpan = submitButton?.querySelector('.buy-btn-cs, span');
          
          if (buyBtnSpan) {
            const priceSpan = document.createElement('span');
            priceSpan.id = `price-button-${this.sectionId}`;
            priceSpan.setAttribute('role', 'status');
            priceSpan.innerHTML = `
              <div class="price price--large price--show-badge">
                <div class="price__container">
                  <div class="price__regular">
                    <span class="visually-hidden visually-hidden--inline">Regular price</span>
                    <span class="price-item price-item--regular">$0.00</span>
                  </div>
                  <div class="price__sale">
                    <span class="visually-hidden visually-hidden--inline">Sale price</span>
                    <span class="price-item price-item--sale price-item--last">$0.00</span>
                  </div>
                </div>
              </div>
            `;
            buyBtnSpan.appendChild(document.createTextNode(' - '));
            buyBtnSpan.appendChild(priceSpan);
            buttonPriceElement = priceSpan;
          }
        }

        if (!buttonPriceElement) return;

        // Format price from cents to dollars
        const price = variant.price / 100;
        const compareAtPrice = variant.compare_at_price ? variant.compare_at_price / 100 : null;
        const formattedPrice = `$${price.toFixed(2)}`;
        const formattedComparePrice = compareAtPrice ? `$${compareAtPrice.toFixed(2)}` : null;

        // Update all regular price items
        const regularPriceItems = buttonPriceElement.querySelectorAll('.price-item--regular:not(s .price-item--regular)');
        regularPriceItems.forEach(item => {
          if (!item.closest('s')) {
            item.textContent = formattedPrice;
          }
        });

        // Update sale price items if on sale
        const salePriceItems = buttonPriceElement.querySelectorAll('.price-item--sale');
        salePriceItems.forEach(item => {
          item.textContent = formattedPrice;
        });

        // Update compare at price items
        if (formattedComparePrice) {
          const comparePriceItems = buttonPriceElement.querySelectorAll('s .price-item--regular');
          comparePriceItems.forEach(item => {
            item.textContent = formattedComparePrice;
          });
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      get sectionId() {
        return this.dataset.sectionId;
      }
    }
  );
}
